import React, { useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Card, ListItem, ListItemText } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import Container from "@material-ui/core/Container";
import List from "@material-ui/core/List";
import IconButton from "@material-ui/core/IconButton";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import { join, pluck } from "ramda";
import EditIcon from "@material-ui/icons/EditOutlined";
import { useDeleteUserOrganization } from "./userServices";
import AddOrganizationDialog from "./AddOrganizationDialog";
import EditUserOrganizationDialog from "./EditUserOrganizationDialog";
import ConfirmButton from "../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 400,
    padding: theme.spacing(3),
    margin: theme.spacing(1),
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },

  newButton: {
    textTransform: "uppercase",
    color: "#127DA9",
    fontSize: "1.2rem",
    fontWeight: "bold",
  },

  listItem: {
    borderBottomColor: theme.palette.primary.dark,
    borderBottomStyle: "dashed",
    borderBottomWidth: 1,
  },
}));

const UserOrganizationsCard = ({ user, onChange, onAlert }) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditEditDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const deleteOrganization = useDeleteUserOrganization();
  const handleDeleteOrganization = useCallback(
    async organizationId => {
      try {
        await deleteOrganization.mutateAsync({
          resourceIds: {
            userId: user.userId,
            organizationId,
          },
        });
        onChange();
      } catch (err) {
        onAlert("error", [err.message]);
      }
    },
    [deleteOrganization, user, onAlert, onChange],
  );

  const handleEditClicked = useCallback(organization => {
    setSelectedOrganization(organization);
    setEditEditDialogOpen(true);
  }, []);

  const handleAddSuccess = useCallback(() => {
    onChange();
    setDialogOpen(false);
  }, [onChange]);

  const handleEditSuccess = useCallback(() => {
    onChange();
    setEditEditDialogOpen(false);
  }, [onChange]);

  const handleError = useCallback(
    error => {
      onAlert("error", [error.message]);
    },
    [onAlert],
  );

  return (
    <Card classes={{ root: classes.root }}>
      <AddOrganizationDialog
        open={dialogOpen}
        onCancel={useCallback(() => setDialogOpen(false), [])}
        onOk={handleAddSuccess}
        onError={handleError}
        user={user}
      />
      <EditUserOrganizationDialog
        open={editDialogOpen}
        onCancel={useCallback(() => setEditEditDialogOpen(false), [])}
        onOk={handleEditSuccess}
        onError={handleError}
        user={user}
        organization={selectedOrganization}
      />
      <Box className={classes.topBar}>
        <Box style={{ marginBottom: "10px" }}>
          <Typography variant="h2" color="primary" noWrap>
            User Organizations
          </Typography>
        </Box>

        <Grid container spacing={3} direction="row" justify="flex-end">
          <Grid item>
            <Button
              variant={"text"}
              startIcon={<AddIcon />}
              classes={{ root: classes.newButton }}
              onClick={useCallback(() => setDialogOpen(true), [])}
            >
              Add User Organization
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Container maxWidth={false}>
        <List dense={true}>
          {user.organizations.map(org => (
            <ListItem
              classes={{ root: classes.listItem }}
              key={org.organizationId}
            >
              <ListItemText
                primary={org.organizationName}
                secondary={org.organizationId}
              />
              <ListItemText
                primary={join(
                  ", ",
                  pluck("roleName", org.userOrganizationRoles),
                )}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleEditClicked(org)}
                >
                  <EditIcon />
                </IconButton>
                <ConfirmButton
                  handleDelete={() =>
                    handleDeleteOrganization(org.organizationId)
                  }
                  dialogTitle="Remove Property"
                  dialogBody="Are you sure you want to remove this property?"
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Container>
    </Card>
  );
};

export default UserOrganizationsCard;
